import {isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {LinksColumnSliceLinksColumnV2} from 'prismicio-types'
import {track} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {LinkItem} from './components/LinkItem'
import {PromoCard} from './components/PromoCard'

const LinksColumnV2 = ({
  slice,
  context: {shouldHideTitle = false, pageUid},
  componentIndex,
}: {
  slice: LinksColumnSliceLinksColumnV2
  context: {shouldHideTitle?: boolean; pageUid: string}
  componentIndex: number
}): JSX.Element => {
  const domainCountry = useGetDomainCountry()
  const {linkResolver} = useLinkResolver()

  const regularLinks = slice.primary.links.filter((link) => !link.is_feature)
  const featureLinks = slice.primary.links.filter((link) => link.is_feature)

  return (
    <div
      className="col-span-full flex w-full flex-col px-6 py-8 text-black max-lg:p-0 lg:gap-2 lg:gap-y-8 lg:border-r-[1px] lg:border-black/[10%] lg:pt-6"
      data-slice-variation={slice.variation}
    >
      {isFilled.keyText(slice.primary.column_title) && (
        <span
          className={cn(
            'lg:text-tg-body-l w-full pb-4 text-[12px] leading-[14px] max-lg:px-4 max-lg:font-semibold max-lg:uppercase max-lg:text-moss-grey-400 lg:pb-0 lg:font-normal',
            {
              'max-lg:hidden': shouldHideTitle,
            },
          )}
        >
          {slice.primary.column_title}
        </span>
      )}
      <div className="flex h-full flex-col justify-between gap-4 max-lg:pb-6 lg:gap-8">
        <ul className="grid w-full list-none grid-cols-1 gap-x-4 gap-y-3 max-lg:divide-y max-lg:divide-black/[10%] lg:gap-y-6">
          {regularLinks.map((link, index) => {
            if (!isFilled.link(link.link_url)) {
              return null
            }

            return (
              <LinkItem
                key={`link-${componentIndex}-${index}`}
                link={link}
                index={index}
                componentIndex={componentIndex}
                marketingId={slice.primary.marketing_id}
                pageUid={pageUid}
                domainCountry={domainCountry}
                isFirst={index === 0}
              />
            )
          })}
        </ul>
        {featureLinks.map((link, index) => {
          if (!isFilled.link(link.link_url)) {
            return null
          }

          return (
            <PrismicNextLink
              key={`feature-${link.link_label}-${index}`}
              href={linkResolver(link.link_url)}
              onClick={() => {
                track({
                  event: 'website_click',
                  dataType: `order-${componentIndex}`,
                  location: 'main-nav-drop-down-feature',
                  mktgId: `category-${slice.primary.marketing_id}-feature-${index}`,
                  pageName: pageUid,
                })
              }}
              className="block rounded-[4px] bg-green-dark p-5 transition-opacity hover:opacity-90 max-lg:mx-4"
            >
              {isFilled.keyText(link.feature_eyebrow) && (
                <span className="text-[11px] font-semibold uppercase tracking-widest text-green-neon">
                  {link.feature_eyebrow}
                </span>
              )}
              <div className="mt-1.5 flex flex-col gap-1">
                <span className="text-[18px] font-normal text-moss-white">{link.link_label}</span>
                {isFilled.keyText(link.link_description) && (
                  <span className="text-[13px] leading-snug text-moss-white/60">{link.link_description}</span>
                )}
              </div>
            </PrismicNextLink>
          )
        })}
        {isFilled.group(slice.primary.promo_card) &&
          slice.primary.promo_card.length > 0 &&
          isFilled.link(slice.primary.promo_card[0].link) && (
            <PromoCard
              promoCard={slice.primary.promo_card[0]}
              componentIndex={componentIndex}
              pageUid={pageUid}
              domainCountry={domainCountry}
            />
          )}
      </div>
    </div>
  )
}

export default LinksColumnV2
